import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Card, Chip, Sheet, Table, Tooltip, Typography } from '@mui/joy';
import { Info, Layers } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';
const REFRESH_MS = 60 * 1000;
// The firmware manager re-sends every 15 minutes even when nothing changed.
const STALE_AFTER_MS = 20 * 60 * 1000;

const STATE_COLORS = {
    running: 'success',
    restarting: 'warning',
    exited: 'neutral',
    missing: 'danger',
    dead: 'danger',
};

const imageTag = (image) => image.split('/').pop();

const formatAgo = (ms) => {
    const seconds = Math.max(0, Math.round((Date.now() - ms) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
};

/** Version of each hub container and the firmware manager, as last reported by the hub. */
const HubComponentVersions = ({ hubId }) => {
    const { session } = useAuth();
    const [items, setItems] = useState([]);
    const [reportedAt, setReportedAt] = useState(null);
    const [error, setError] = useState(null);
    const sessionRef = useRef(session);
    sessionRef.current = session;

    const load = useCallback(async () => {
        if (!sessionRef.current?.accessToken) return;
        try {
            const res = await fetch(`${API_BASE}/device/${hubId}/components`, {
                headers: {
                    Authorization: sessionRef.current.accessToken,
                    'Vendor-Id': sessionRef.current.vendorId,
                },
            });
            const body = await res.json().catch(() => null);
            if (!res.ok) {
                setError(body?.error || `Failed to load component versions (${res.status}).`);
                return;
            }
            setError(null);
            setItems(body?.items || []);
            setReportedAt(body?.reported_at || null);
        } catch (e) {
            setError('Unable to reach the admin API.');
        }
    }, [hubId]);

    useEffect(() => {
        load();
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [load, session?.accessToken]);

    const stale = reportedAt && Date.now() - reportedAt > STALE_AFTER_MS;

    return (
        <Box sx={{ mb: 4 }}>
            <Typography level="h3" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Layers sx={{ mr: 1 }} />
                Component Versions
                {reportedAt && (
                    <Typography level="body-sm" textColor={stale ? 'warning.600' : 'neutral.500'} sx={{ ml: 2 }}>
                        reported {formatAgo(reportedAt)}
                        {stale ? ' — hub may be offline' : ''}
                    </Typography>
                )}
            </Typography>
            {error && (
                <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {items.length === 0 ? (
                <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                    <Info sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                    <Typography level="body-sm" textColor="neutral.500">
                        This hub has not reported its components yet (needs an updated firmware manager)
                    </Typography>
                </Card>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                    <Box sx={{ overflow: 'auto' }}>
                        <Table hoverRow size="sm" sx={{ minWidth: 760 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 170 }}>Component</th>
                                    <th style={{ width: 110 }}>Version</th>
                                    <th>Image</th>
                                    <th style={{ width: 110 }}>State</th>
                                    <th>Status</th>
                                    <th style={{ width: 180 }}>Started</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.service}>
                                        <td>
                                            <Typography level="body-sm" fontWeight="md">{item.service}</Typography>
                                        </td>
                                        <td>
                                            <Chip size="sm" variant="soft" color="primary">{item.version}</Chip>
                                        </td>
                                        <td>
                                            <Tooltip
                                                title={[item.image, item.image_digest].filter(Boolean).join('\n') || 'Host process'}
                                                variant="outlined"
                                                sx={{ whiteSpace: 'pre-line' }}
                                            >
                                                <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                                                    {item.image ? imageTag(item.image) : 'host process'}
                                                </Typography>
                                            </Tooltip>
                                        </td>
                                        <td>
                                            <Chip size="sm" variant="soft" color={STATE_COLORS[item.state] || 'neutral'}>
                                                {item.state}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Typography level="body-sm">{item.status || '—'}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-sm">
                                                {item.started_at ? new Date(item.started_at).toLocaleString() : '—'}
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Box>
                </Sheet>
            )}
        </Box>
    );
};

export default HubComponentVersions;
